import * as React from "react";
import { Suspense } from "react";
import { Badge, Button, Col, Image, Row, Table } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { InView } from "react-intersection-observer";
import { useComparisonTableQuery } from "../../../hooks/useComparisonTableQuery";
import { useComparisonIdFragment } from "../../../hooks/useComparisonIdFragment";
import { ErrorBoundary } from "@sentry/react";
import { StarRating } from "../../../components/starRating";
import { useAppContext } from "../../../hooks/useAppContext";
import { useCartService } from "../../../hooks/addToCart/useCartService";
import Skeleton from "react-loading-skeleton";
import { pushToDataLayer } from "../../../hooks/useCrossSellDataLayerPush";

export const ComparisonTable = () => {
    const { complete, comparisonId } = useComparisonIdFragment();

    if (!complete || !comparisonId || isMobile) return <></>;

    return (
        <ErrorBoundary fallback={<></>}>
            <InView triggerOnce={true}>
                {({ inView, ref }) => (
                    <Row ref={ref} className="mx-auto d-block my-3">
                        <Col>
                            {inView && <Suspense
                                fallback={<><Skeleton height={74} style={{ width: '50%' }} className="p-3" /><Skeleton
                                    height={435.63} /></>}><Content /></Suspense>}
                        </Col>
                    </Row>
                )}
            </InView>
        </ErrorBoundary>
    )
}

const Content = () => {
    const { productId, changeProduct } = useAppContext();
    const {
        model: {
            breakOutEarly,
            comparisonTable: {
                specifications,
                items
            }
        }
    } = useComparisonTableQuery();

    const { createItem, setShow } = useCartService();

    // Initialize an empty bundle array
    let products: Array<{
        title: string;
        sku: string;
        price: number;
        listType: string;
    }> = [];

    // Populate the bundle array
    if (items.length > 0) {
        items.forEach((item) => {
            // Check if the item properties are defined
            if (item.title && item.sku && item.price) {
                products.push({
                    title: item.title,
                    sku: item.sku,
                    price: item.price,
                    listType: 'ProductNew | ComparisonTable', 
                });
            }
        });

        // Push the bundle data to the data layer
        pushToDataLayer(products);
    }


    if (breakOutEarly) return <></>;

    return (
        <div className="mt-4 ">
            <div className="fs-3 p-3">Compare with Similar Items</div>
            <Table id="product-comparison-table" responsive className="mb-0 rounded border">
                <thead className="bg-white border-light">
                    <tr>
                        <td aria-hidden="true" className="bg-light"></td>
                        {items.map((item, index) => (
                            <th key={item.productId}
                                className={`text-center bg-light ${item.isCurrentItem(productId) && 'this-product position-relative'}`}>
                                <a href={item.isCurrentItem(productId) ? undefined : item.path} onClick={(e) => {
                                    // Prevents the link
                                    // from being followed
                                    // to allow the request
                                    // to be made locally without a page refresh
                                    e.preventDefault();

                                    // Prevents the link from being followed to the current page
                                    if (item.isCurrentItem(productId)) return;

                                    changeProduct(item.productId, item.primaryId, item.path);
                                }}
                                    tabIndex={0} role="link"
                                    aria-label={`${!item.isCurrentItem(productId) && 'Link to Product Details Page'}`}
                                    data-item-id={productId}
                                    data-position={index}
                                    data-sku={item.sku}
                                    data-ee-list={`ProductNew | ComparisonTable`}
                                    data-react="true"
                                    className="product-link">
                                    {item.isCurrentItem(productId) && (
                                        <Badge bg="success"
                                            className="position-absolute top-0 start-0 rounded-0 fs-6"
                                            style={{ zIndex: '99' }}>
                                            This Product
                                        </Badge>
                                    )}
                                    <Image src={item.imageSrc}
                                        alt=""
                                        aria-hidden
                                        title={item.imageTitle}
                                        height="100"
                                        loading="lazy"
                                        decoding="async"
                                        className="img-fluid img-thumbnail py-2" />
                                    <div
                                        className={`d-block fw-normal pt-2 px-3 title-container ${item.isCurrentItem(productId) && "mb-3"}`}
                                        style={{ minHeight: '75px', maxHeight: '75px', overflow: 'hidden' }}>
                                        {item.truncatedTitle && item.truncatedTitle}
                                    </div>
                                </a>
                                {!item.isCurrentItem(productId) && (
                                    <Button variant="warning"
                                        className="add-to-cart-btn my-2"
                                        style={{ width: '75%' }}
                                        size="sm"
                                        href={item.href}
                                        data-ee-list={`ProductNew | ComparisonTable`}
                                        data-item-id={productId}
                                        data-quantity={item.minQuantity}
                                        data-sku={item.sku}
                                        onClick={async (e) => {
                                            // Prevents the link
                                            // from being followed
                                            // to allow the request
                                            // to be made locally without a page refresh
                                            e.preventDefault();

                                            if (item.customizeIt) {
                                                setShow(false);
                                                return changeProduct(item.productId, item.primaryId, item.path);
                                            }
                                            await createItem({
                                                productId: item.productId,
                                                quantity: null,
                                                artworkId: null,
                                                artworkName: null,
                                                artworkNotes: null,
                                                width: null,
                                                height: null
                                            });
                                        }}>
                                        {item.customizeIt ? 'Customize It' : 'Add to Cart'}
                                    </Button>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="fw-bold px-5">Customer Rating</td>
                        {items.map((item) => (
                            <td key={item.productId} className={item.isCurrentItem(productId) ? 'this-product' : ''}>
                                <div className="text-center">
                                    {item.ratingStatistics.count > 0 ?
                                        <>
                                            <StarRating grade={item.ratingStatistics.mean} />
                                            <small className="text-muted "> ({item.ratingStatistics.count})</small>
                                        </> :
                                        <small className="text-muted">Not Yet Rated</small>
                                    }
                                </div>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className="fw-bold px-5">Price</td>
                        {items.map((item) => (
                            <td key={item.productId} className={item.isCurrentItem(productId) ? 'this-product' : ''}>
                                <div className="d-flex flex-wrap align-items-center flex-column">
                                    <span className={` fw-bold d-flex align-items-center m-auto text-dark`}>
                                        {item.isDynamicDimension &&
                                            <div><small className="fw-normal me-1 text-muted">Starting at</small></div>}
                                        {item.onSale && <del className="ms-1 fw-normal text-muted fw-normal me-1">
                                            <small>{item.formatted.unitPrice}{" "}</small>
                                        </del>}
                                        {item.formatted.price}
                                    </span>
                                    {item.onSale && (
                                        <small
                                            className="text-danger fw-normal px-1 d-block m-auto">Save {item.salePercentage && Math.round(item.salePercentage)}%</small>
                                    )}
                                </div>
                            </td>
                        ))}
                    </tr>
                    {specifications.map((specification) => (
                        <tr key={specification.typeId}>
                            <td className="fw-bold px-5">{specification.typeName}</td>
                            {items.map((item) => (
                                <td key={item.productId} className={item.isCurrentItem(productId) ? 'this-product' : ''}>
                                    {item.getTypeValue(specification.typeId)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};